import RateReview from "@mui/icons-material/RateReview";
import {
  Box,
  ButtonBase,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import CopyableTypography from "components/Typography/CopyableTypography";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteManifestSource } from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  COMPARE_SOURCE_ORDER,
  computeOutlierSources,
  filterRowsByDifferences,
  getSourceColorKey,
  getSourceLabel,
  groupCompareRows,
  MANIFEST_GROUP_LABEL,
  SOURCE_DOT_COLOR,
} from "./CompareSourcesDialog.helpers";

/** A single comparison row, the presentational shape the table renders. */
export interface CompareSourcesTableRow {
  key: string;
  group: string;
  field: string;
  label: string;
  eManifest: string | null;
  vendor: string | null;
  document: string | null;
  differs: boolean;
  missingInSomeSource: boolean;
  /** Sources present for THIS row's line; drives per-cell absent-vs-gap rendering. */
  availableSources: WasteManifestSource[];
  /** The document's extracted value while its document still awaits review. */
  documentPending: string | null;
}

/** The raw comparison row shape both compare queries return: the three source
 * fields are nullable/optional in GraphQL, the rest required. */
export interface CompareSourcesRawRow {
  key: string;
  group: string;
  field: string;
  label: string;
  eManifest?: string | null;
  vendor?: string | null;
  document?: string | null;
  differs: boolean;
  missingInSomeSource: boolean;
  availableSources: WasteManifestSource[];
  documentPending?: string | null;
}

/**
 * Map raw GraphQL comparison rows (whose nullable string fields are optional) to
 * the table's presentational row shape (which uses `string | null`). Shared by
 * the manifest- and stream-level compare dialogs.
 */
export function toCompareSourcesTableRows(
  rawRows: ReadonlyArray<CompareSourcesRawRow>
): CompareSourcesTableRow[] {
  return rawRows.map((row) => ({
    key: row.key,
    group: row.group,
    field: row.field,
    label: row.label,
    eManifest: row.eManifest ?? null,
    vendor: row.vendor ?? null,
    document: row.document ?? null,
    differs: row.differs,
    missingInSomeSource: row.missingInSomeSource,
    availableSources: row.availableSources ?? [],
    documentPending: row.documentPending ?? null,
  }));
}

export interface CompareSourcesTableProps {
  rows: CompareSourcesTableRow[];
  availableSources: WasteManifestSource[];
  differenceCount: number;
  missingCount: number;
  loading: boolean;
  /** When false, every group renders its section header (stream view has no
   *  special leading manifest group). Defaults to true to preserve the manifest
   *  dialog's current behavior where the "Manifest" group leads un-headered. */
  hasManifestGroup?: boolean;
  /** Optional deep-link highlight (manifest dialog only). */
  highlightField?: string;
  /** Compresses the field column when an adjacent rail (e.g. PDF) is open. */
  compact?: boolean;
  /**
   * The manifest has a source document nobody has reviewed yet. Its column is
   * not a source of record — it stays out of every count and highlight — but it
   * renders the extracted values under a provisional treatment instead of the
   * absent-source wash, so the customer can see what their review is holding up.
   */
  hasUnreviewedDocument?: boolean;
  /** Opens the document review. Omitted where there is no single manifest to
   *  review (the stream view), which also drops the header's review button. */
  onOpenReview?: () => void;
}

/** Fields whose raw values are ISO dates and should render in our M/D/YYYY format. */
const COMPARE_DATE_FIELDS = new Set(["shippedDate", "receivedDate"]);

/** The stringified value a row holds for a given source column. */
function rowValueForSource(
  row: CompareSourcesTableRow,
  source: WasteManifestSource
): string | null {
  const raw = (() => {
    switch (source) {
      case WasteManifestSource.EManifest:
        return row.eManifest ?? null;
      case WasteManifestSource.Vendor:
        return row.vendor ?? null;
      case WasteManifestSource.ManifestDocument:
        return row.document ?? null;
    }
  })();
  // Normalize date fields to M/D/YYYY so they don't render as full datetimes
  // (and so equal dates stored in different ISO precisions stop reading as a
  // difference).
  if (raw != null && COMPARE_DATE_FIELDS.has(row.field)) {
    return getUtcDisplayDate(raw);
  }
  return raw;
}

const STICKY_HEADER_HEIGHT = "2.5rem";

/**
 * One source column header. Absent sources (no such pillar on the manifest) are
 * muted and labeled so an empty column reads as "not a source here". A document
 * awaiting review is neither available nor absent: it reads as pending, in the
 * same amber the Document Review chip uses on the grid, and offers the way in.
 */
function SourceColumnHeader({
  source,
  borderColor,
  available,
  pending = false,
  onOpenReview,
}: {
  source: WasteManifestSource;
  borderColor: string;
  available: boolean;
  pending?: boolean;
  onOpenReview?: () => void;
}) {
  const theme = useTheme();
  // Bright/neon dot color per source (no palette roles for these — see helper).
  const dotColor = SOURCE_DOT_COLOR[getSourceColorKey(source)];

  const dotBg = (() => {
    if (available) return dotColor;
    if (pending) return theme.palette.warning.main;
    return theme.palette.action.disabled;
  })();

  const headerColor = (() => {
    if (available) return undefined;
    if (pending) return theme.palette.warning.dark;
    return "text.disabled";
  })();

  return (
    <TableCell
      sx={{
        fontWeight: 600,
        borderLeft: `1px solid ${borderColor}`,
        color: headerColor,
      }}
    >
      {/* Dot, label, and the state hint sit inline on one row (no wrap), so the
          hint reads to the right of the source name. */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ whiteSpace: "nowrap" }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: dotBg,
            flexShrink: 0,
          }}
        />
        <span>{getSourceLabel(source)}</span>
        {!available && !pending && (
          <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 400 }}>
            (no data)
          </Typography>
        )}
        {pending && (
          <>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.warning.dark, fontWeight: 400 }}
            >
              Needs review
            </Typography>
            {onOpenReview && (
              <Tooltip title="Review this document">
                <IconButton
                  size="small"
                  onClick={onOpenReview}
                  aria-label="Review this document"
                  sx={{ color: theme.palette.warning.dark, p: 0.25 }}
                >
                  <RateReview fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Stack>
    </TableCell>
  );
}

/**
 * The provisional cell under an unreviewed document column: amber-washed,
 * italic, and never click-to-copy — the value is not yet anybody's source of
 * record, so it offers none of the affordances a real one does.
 */
function PendingDocumentCell({
  value,
  borderColor,
}: {
  value: string | null;
  borderColor: string;
}) {
  const theme = useTheme();
  return (
    <TableCell
      sx={{
        wordBreak: "break-word",
        verticalAlign: "top",
        borderLeft: `2px dashed ${alpha(theme.palette.warning.main, 0.5)}`,
        // Far fainter than the table's difference flash: amber is already spent
        // on divergence here, so pending earns its reading from FORM — dashed
        // spine, italic values, a wash too pale to be mistaken for a highlight.
        // The hue stays amber to match the Document Review chip on the grid,
        // which is where the customer first met this state.
        bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.12 : 0.07),
        borderRight: `1px solid ${borderColor}`,
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontStyle: "italic", color: value ? undefined : "text.disabled" }}
      >
        {value ?? "—"}
      </Typography>
    </TableCell>
  );
}

/**
 * Presentational comparison table for waste-manifest data sources. Takes its
 * rows + counts as props (no Apollo query inside) so it can back both the
 * manifest compare dialog and the stream view. All table-local UI state — the
 * "show only differences" toggle, the audit-stat highlight, and the
 * scroll-into-view for a deep-linked field — lives here.
 */
export function CompareSourcesTable({
  rows,
  availableSources,
  differenceCount,
  missingCount,
  loading,
  hasManifestGroup = true,
  highlightField,
  compact = false,
  hasUnreviewedDocument = false,
  onOpenReview,
}: CompareSourcesTableProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Section/column headers get the grey background; data rows are white by
  // default (point: invert the old all-grey table so group starts read clearly).
  const headerBg = isDark ? theme.palette.grey[900] : theme.palette.grey[100];
  const subtleSurfaceBg = isDark ? theme.palette.grey[900] : theme.palette.grey[50];
  const stickyBg = theme.palette.background.paper;
  // Single consistent divider color for the full table grid + outer border.
  const gridBorder = theme.palette.divider;
  // Accent color used to flag divergence: a left spine on a differing row and a
  // dotted underline beneath the specific outlier value(s).
  const diffAccent = theme.palette.warning.main;
  // Pale-orange wash painted on matching cells (and the "on" stat pill) while a
  // stat highlight toggle is active.
  const flashBg = alpha(theme.palette.warning.main, isDark ? 0.32 : 0.2);
  // Opaque highlight tint for the scrolled-to field row (?compare=field:<name>).
  const highlightBg = isDark
    ? darken(theme.palette.primary.main, 0.72)
    : lighten(theme.palette.primary.main, 0.85);

  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);
  // Which stat is toggled "on": persistently washes the matching cells (the
  // diverging values, or the missing gaps) until toggled off or switched.
  const [highlight, setHighlight] = useState<"differences" | "missing" | null>(null);
  const toggleHighlight = useCallback(
    (kind: "differences" | "missing") => setHighlight((cur) => (cur === kind ? null : kind)),
    []
  );

  // All three source columns are always shown — that's the point of the
  // comparison. This set tracks which pillars actually exist for the manifest
  // so we can tell a real per-field gap ("this source has this field, that one
  // doesn't") apart from an entirely-absent source (no e-Manifest at all). Only
  // the former counts as "missing". Defaults to all three while loading so a
  // value never momentarily reads as an absent-source placeholder.
  // A document-only manifest awaiting review genuinely HAS no source of record,
  // so an empty list alone cannot mean "not loaded yet" — treating it that way
  // lights up all three columns and buries the pending document under a row of
  // em dashes. But a query that failed also resolves to nothing, and labelling
  // every column "(no data)" would state as fact something we never learned.
  // Rows are what separates them: the pending manifest has them, a failure
  // does not.
  const availableSourceSet = useMemo<Set<WasteManifestSource>>(() => {
    if (loading || rows.length === 0) return new Set(COMPARE_SOURCE_ORDER);
    return new Set(availableSources);
  }, [availableSources, loading, rows.length]);

  const visibleRows = useMemo(
    () => filterRowsByDifferences(rows, showOnlyDifferences),
    [rows, showOnlyDifferences]
  );

  const groupedRows = useMemo(() => groupCompareRows(visibleRows), [visibleRows]);

  // Scroll the highlighted field row into view once data is loaded.
  const highlightedRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (!highlightField || loading) return;
    const node = highlightedRowRef.current;
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightField, loading, visibleRows]);

  const COL_COUNT = 1 + COMPARE_SOURCE_ORDER.length; // field + all three sources

  const renderValueCell = (
    row: CompareSourcesTableRow,
    source: WasteManifestSource,
    isOutlier: boolean
  ) => {
    // A pending document column shows its extracted value in place of the
    // absent-source wash. It is never an outlier and never a gap: the value is
    // not a source of record, so it takes no part in the comparison — only the
    // provisional styling below marks it, and the header says why.
    const isPendingDocumentCell =
      hasUnreviewedDocument &&
      source === WasteManifestSource.ManifestDocument &&
      !row.availableSources.includes(source);
    if (isPendingDocumentCell) {
      return (
        <PendingDocumentCell key={source} value={row.documentPending} borderColor={gridBorder} />
      );
    }

    const value = rowValueForSource(row, source);
    const hasValue = value != null;
    // Per-row availability (not the global union): a source the COLUMN exists
    // for can still be absent for THIS row's line. Hatching it here keeps the
    // cell consistent with this row's `missingInSomeSource` / `missingCount`,
    // so a real gap (available source, no value) is the only thing showing "—".
    const sourceAvailable = row.availableSources.includes(source);
    // Two distinct empty states:
    //  - absent source: this manifest has no such pillar at all (its whole
    //    column is blank). Expected, not a data gap — never counted/highlighted.
    //  - gap: an available source simply lacks this field while another has it.
    //    This is what "missing from one or more sources" counts and highlights.
    const isGap = !hasValue && sourceAvailable;
    const isHighlighted =
      (highlight === "differences" && isOutlier) ||
      (highlight === "missing" && isGap && row.missingInSomeSource);
    return (
      <TableCell
        key={source}
        sx={{
          wordBreak: "break-word",
          verticalAlign: "top",
          borderLeft: `1px solid ${gridBorder}`,
          transition: "background-color 0.25s ease",
          ...(isHighlighted ? { bgcolor: flashBg } : {}),
          // Absent-source cells get a faint striped wash so the whole column
          // reads as "not a source here" rather than a field-level gap.
          ...(!sourceAvailable
            ? {
                backgroundImage: `repeating-linear-gradient(45deg, ${gridBorder} 0 1px, transparent 1px 7px)`,
                opacity: 0.6,
              }
            : {}),
        }}
      >
        {hasValue ? (
          // Click-to-copy value. Outliers keep the normal text color (so they
          // stay readable) but are bumped to medium weight and marked with a
          // dotted accent underline — a "spot the difference" cue rather than a
          // flat fill or recolor.
          <CopyableTypography
            text={value}
            variant="body2"
            stopPropagation={false}
            sx={
              isOutlier
                ? {
                    fontWeight: 600,
                    textDecoration: "underline dotted",
                    textDecorationColor: diffAccent,
                    textDecorationThickness: "2px",
                    textUnderlineOffset: "3px",
                  }
                : undefined
            }
          />
        ) : (
          // A gap (available source, no value) shows an em dash; an absent
          // source shows nothing — the striped wash already conveys it.
          <Typography variant="body2" sx={{ color: "text.disabled" }}>
            {sourceAvailable ? "—" : ""}
          </Typography>
        )}
      </TableCell>
    );
  };

  const renderRow = (row: CompareSourcesTableRow) => {
    const isHighlighted = highlightField != null && row.field === highlightField;
    // Background is reserved for the deep-link highlight only; divergence is
    // shown with a left spine + per-value underline, not a row fill.
    const rowBg = isHighlighted ? highlightBg : undefined;

    // Which source(s) hold the diverging value, so we can mark just those cells.
    // Absent sources contribute null and are ignored by the outlier logic.
    const outlierSources = computeOutlierSources(
      COMPARE_SOURCE_ORDER.map((source) => ({ source, value: rowValueForSource(row, source) }))
    );

    return (
      <TableRow
        key={row.key}
        ref={isHighlighted ? highlightedRowRef : undefined}
        sx={{
          ...(rowBg != null ? { bgcolor: rowBg } : {}),
          "& .MuiTableCell-body": { verticalAlign: "top" },
        }}
      >
        <TableCell
          component="th"
          scope="row"
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 1,
            // Field labels stay at normal weight; divergence is flagged by the
            // left accent spine here and the underlined value cells, so the
            // label itself doesn't also need to bold (too much bold otherwise).
            fontWeight: 400,
            ...(compact ? {} : { width: 230, whiteSpace: "nowrap" }),
            bgcolor: rowBg ?? stickyBg,
            // Left accent spine flags the whole row as having a divergence,
            // scannable down the left edge of the table.
            ...(row.differs ? { borderLeft: `3px solid ${diffAccent}` } : {}),
          }}
        >
          {row.label}
        </TableCell>
        {COMPARE_SOURCE_ORDER.map((source) =>
          renderValueCell(row, source, outlierSources.has(source))
        )}
      </TableRow>
    );
  };

  // A single audit stat. When its count is non-zero it's a toggle: clicking it
  // highlights the matching cells in the table and keeps them highlighted until
  // it's clicked again (or the other stat is clicked). The "on" state reads as
  // a filled amber pill.
  const renderStat = (count: number, label: string, kind: "differences" | "missing") => {
    const clickable = count > 0;
    const active = highlight === kind;
    const chip = (
      <ButtonBase
        disabled={!clickable}
        onClick={() => toggleHighlight(kind)}
        focusRipple
        aria-pressed={active}
        sx={{
          borderRadius: 1,
          px: 0.75,
          py: 0.25,
          cursor: clickable ? "pointer" : "default",
          border: "1px solid",
          borderColor: active ? "warning.main" : "transparent",
          bgcolor: active ? flashBg : "transparent",
          transition: "background-color 0.2s ease",
          "&:hover": clickable && !active ? { bgcolor: "action.hover" } : undefined,
        }}
      >
        <Typography
          variant="caption"
          color={active ? "warning.dark" : "text.secondary"}
          component="span"
          sx={{ fontWeight: active ? 600 : undefined }}
        >
          <Box
            component="span"
            sx={{ fontWeight: 700, color: active ? "warning.dark" : "text.primary" }}
          >
            {count}
          </Box>{" "}
          {label}
          {active && " ✓"}
        </Typography>
      </ButtonBase>
    );
    if (!clickable) return chip;
    return (
      <Tooltip title={active ? "Clear highlight" : "Highlight these cells in the table"}>
        {chip}
      </Tooltip>
    );
  };

  // Quiet inline audit summary, with the "show only differences" toggle on the
  // trailing edge. Rendered above the table and kept visible during loading.
  const auditSummary = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ px: 0, pt: 0.5, pb: 0.25, flexShrink: 0 }}
    >
      {renderStat(
        differenceCount,
        differenceCount === 1 ? "difference" : "differences",
        "differences"
      )}
      {renderStat(missingCount, "missing from one or more sources", "missing")}
      <Box sx={{ flex: 1 }} />
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={showOnlyDifferences}
            onChange={(e) => setShowOnlyDifferences(e.target.checked)}
          />
        }
        label={<Typography variant="body2">Show only differences</Typography>}
      />
    </Stack>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      {auditSummary}
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {loading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} variant="rectangular" height={32} />
            ))}
          </Stack>
        ) : (
          <TableContainer sx={{ overflow: "visible", px: 0, pt: 0, pb: 2 }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                border: `1px solid ${gridBorder}`,
                borderCollapse: "separate",
                borderSpacing: 0,
                // Fixed layout: the Field column takes a fixed width and the
                // three source columns split the rest equally — so they stay
                // equal even when a source has no data.
                width: "100%",
                tableLayout: "fixed",
                // Single header divider, flush against the first row (no gap / double line).
                "& .MuiTableHead-root .MuiTableCell-head": {
                  top: 0,
                  height: STICKY_HEADER_HEIGHT,
                  bgcolor: headerBg,
                  borderBottom: `1px solid ${gridBorder}`,
                },
                "& .MuiTableCell-root": { borderBottom: `1px solid ${gridBorder}` },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      // With the PDF rail open, space is tight — let the Field
                      // column collapse to an equal share like the rest.
                      ...(compact ? {} : { width: 230, whiteSpace: "nowrap" }),
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      bgcolor: headerBg,
                    }}
                  >
                    Field
                  </TableCell>
                  {COMPARE_SOURCE_ORDER.map((source) => (
                    <SourceColumnHeader
                      key={source}
                      source={source}
                      borderColor={gridBorder}
                      available={availableSourceSet.has(source)}
                      pending={
                        hasUnreviewedDocument &&
                        source === WasteManifestSource.ManifestDocument &&
                        !availableSourceSet.has(source)
                      }
                      onOpenReview={onOpenReview}
                    />
                  ))}
                </TableRow>
              </TableHead>
              {groupedRows.map((groupBlock) => (
                <TableBody key={groupBlock.group}>
                  {/* The manifest-level fields lead the table un-headered; only
                      the per-line-item groups get a section header row. When the
                      table has no manifest group (stream view), every group gets
                      a header. */}
                  {(!hasManifestGroup || groupBlock.group !== MANIFEST_GROUP_LABEL) && (
                    <TableRow>
                      <TableCell
                        colSpan={COL_COUNT}
                        sx={{
                          bgcolor: headerBg,
                          py: 0.5,
                          position: "sticky",
                          top: STICKY_HEADER_HEIGHT,
                          zIndex: 2,
                          // Stronger top rule sets each line item apart from
                          // the block above it.
                          borderTop: `2px solid ${gridBorder}`,
                        }}
                      >
                        {/* Section headers read as overline labels —
                            deliberately unlike the column-header row so
                            "Line 1" can't be mistaken for a field/column
                            heading. */}
                        <Typography
                          variant="overline"
                          sx={{
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            lineHeight: 1.4,
                            color: "text.secondary",
                          }}
                        >
                          {groupBlock.group}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {groupBlock.rows.map(renderRow)}
                </TableBody>
              ))}

              {visibleRows.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell
                      colSpan={COL_COUNT}
                      align="center"
                      sx={{ py: 8, bgcolor: subtleSurfaceBg }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {showOnlyDifferences
                          ? "No differences between sources"
                          : "No comparison data available"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
